import React, { useEffect, useState } from 'react'
import { useAtomValue, useAtom } from 'jotai'
import {
  allSelectedPictogramsAtom,
  selectedPictogramsAtom,
  pictoSizeAtom,
  textSettingsAtom,
} from 'atoms'
import PictogramCard from './PictogramCard'
import { Grid, IconButton, Box, Paper, Typography } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import DeleteIcon from '@mui/icons-material/Delete'
import RemoveIcon from '@mui/icons-material/Backspace'

const PictogramsViewer = () => {
  const selectedPictograms = useAtomValue(allSelectedPictogramsAtom)
  const [selectedPictosById, setSelectedPictograms] = useAtom(
    selectedPictogramsAtom
  )
  const pictoSize = useAtomValue(pictoSizeAtom)
  const textSettings = useAtomValue(textSettingsAtom)
  const { fontSize } = textSettings

  // Calculate item size
  const gridItemWidth = pictoSize + 8
  const gridItemHeight = pictoSize + 4 * fontSize + 8

  // Scroll position state
  const [isFixed, setIsFixed] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const viewerTop = document.getElementById('pictograms-viewer')?.offsetTop
      if (window.scrollY > viewerTop) {
        setIsFixed(true)
      } else {
        setIsFixed(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Action handlers
  const handleDeleteLast = () => {
    if (selectedPictograms.length > 0) {
      setSelectedPictograms(selectedPictosById.slice(0, -1))
    }
  }

  const handleDeleteAll = () => {
    setSelectedPictograms([])
  }

  const hasSelection = selectedPictograms.length > 0
  if (!hasSelection) {
    return null
  }

  return (
    <Box sx={{ padding: 2, margin: '0 auto' }}>
      <Paper
        id="pictograms-viewer"
        elevation={3}
        sx={{
          position: isFixed ? 'fixed' : 'static',
          top: isFixed ? 0 : 'auto',
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 10,
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: 2,
          backgroundColor: '#f9f9f9',
        }}
      >
        {/* Action Buttons Row */}
        <Grid
          container
          spacing={1}
          justifyContent="flex-end"
          sx={{ backgroundColor: '#e0f7fa', padding: 1 }}
        >
          {/* Left-aligned text */}
          <Grid item xs={6}>
            <Typography variant="h1" sx={{ color: '#00796b' }}>
              Frase elaborada
            </Typography>
          </Grid>
          <Grid item>
            <IconButton
              color="primary"
              aria-label="play all"
              sx={{
                backgroundColor: '#ffffff',
                '&:hover': { backgroundColor: '#b2ebf2' },
                borderRadius: '50%',
              }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton
              color="error"
              aria-label="delete all"
              onClick={handleDeleteAll}
              sx={{
                backgroundColor: '#ffffff',
                '&:hover': { backgroundColor: '#ffcdd2' },
                borderRadius: '50%',
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton
              color="secondary"
              aria-label="delete last item"
              onClick={handleDeleteLast}
              sx={{
                backgroundColor: '#ffffff',
                '&:hover': { backgroundColor: '#e0e0e0' },
                borderRadius: '50%',
              }}
            >
              <RemoveIcon />
            </IconButton>
          </Grid>
        </Grid>

        {/* Grid of Pictograms */}
        <Grid
          container
          spacing={0.5}
          sx={{
            marginTop: hasSelection ? 2 : 0,
            justifyContent: 'flex-start',
          }}
        >
          {hasSelection ? (
            selectedPictograms.map(({ uuid }) => (
              <Grid
                item
                key={uuid}
                sx={{
                  width: `${gridItemWidth}px`,
                  height: `${gridItemHeight}px`,
                }}
              >
                <PictogramCard uuid={uuid} />
              </Grid>
            ))
          ) : (
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'stretch',
                justifyContent: 'center',
                textAlign: 'center',
                border: '1px dashed #ccc',
                borderRadius: '8px',
                color: '#666',
                backgroundColor: '#f0f0f0',
                padding: '16px',
                fontSize: `${fontSize}px`,
              }}
            >
              <h2>
                Pulsa en los pictogramas del tablero para construir aquí la
                frase
              </h2>
            </div>
          )}
        </Grid>
      </Paper>
    </Box>
  )
}

export default PictogramsViewer
